import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Briefcase, CalendarDays, Banknote, Send, Loader2, X, Upload, Users } from "lucide-react";
import { useLang } from "@/i18n";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CarrieresPage() {
  const { t } = useLang();
  const [offres, setOffres] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "", cv: "" });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cvRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/offres`).then((r) => setOffres(r.data)).catch(() => {});
  }, []);

  const uploadCv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload-public`, fd);
      setForm((f) => ({ ...f, cv: data.url }));
      toast.success(t("jobs.form.cvok"));
    } catch {
      toast.error(t("reg.photo.error"));
    } finally {
      setUploading(false);
    }
  };

  const postuler = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/offres/${selected.id}/postuler`, {
        nom: form.nom, email: form.email, telephone: form.telephone,
        message: form.message || null, cv: form.cv || null,
      });
      toast.success(t("jobs.form.success"), { description: t("jobs.form.success.x") });
      setSelected(null);
      setForm({ nom: "", email: "", telephone: "", message: "", cv: "" });
    } catch {
      toast.error(t("reg.toast.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="carrieres-page" className="grain bg-[#0A0A0C] text-white min-h-screen">
      <header className="border-b border-[#27272A] bg-[#0A0A0C]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="carrieres-back" className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#C5A059] transition-colors text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={15} /> {t("eventpage.back")}
          </Link>
          <img src="/assets/logo-dark-sm.png" alt="DREAM2REALITY Football Agency" className="h-10 w-auto" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("jobs.eyebrow")}</p>
          <h1 className="font-display text-5xl lg:text-7xl uppercase font-bold leading-[0.95]">
            {t("jobs.title.a")} <span className="text-[#C5A059]">{t("jobs.title.b")}</span>
          </h1>
          <p className="text-base text-[#A1A1AA] mt-6 max-w-2xl leading-relaxed">{t("jobs.text")}</p>
        </motion.div>

        <div className="mt-16 space-y-4">
          {offres.length === 0 && (
            <div className="border border-[#27272A] bg-[#121214] p-16 text-center" data-testid="jobs-empty">
              <Briefcase size={32} className="text-[#C5A059] mx-auto" strokeWidth={1.5} />
              <p className="text-[#A1A1AA] mt-6 text-sm">{t("jobs.empty")}</p>
            </div>
          )}
          {offres.map((o, i) => (
            <motion.article
              key={o.id}
              data-testid={`job-card-${o.id.slice(0, 8)}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="border border-[#27272A] bg-[#121214] p-8 hover:border-[#C5A059]/50 transition-colors duration-300"
            >
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="text-[10px] uppercase tracking-widest border border-[#C5A059]/50 text-[#C5A059] px-2.5 py-1">{o.type_contrat}</span>
                    <span className="flex items-center gap-1.5 text-xs text-[#71717A]"><MapPin size={12} /> {o.lieu}</span>
                    {o.date_limite && <span className="flex items-center gap-1.5 text-xs text-[#71717A]"><CalendarDays size={12} /> {t("jobs.deadline")} : {o.date_limite}</span>}
                  </div>
                  <h2 className="font-display text-2xl uppercase font-bold">{o.titre}</h2>
                  <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed whitespace-pre-line">{o.description}</p>
                  {o.profil && (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">{t("jobs.profile")}</p>
                      <p className="text-sm text-[#A1A1AA] leading-relaxed whitespace-pre-line">{o.profil}</p>
                    </div>
                  )}
                  {o.salaire && <p className="flex items-center gap-2 text-sm text-[#C5A059] mt-4"><Banknote size={14} /> {o.salaire}</p>}
                </div>
                <button
                  onClick={() => setSelected(o)}
                  data-testid={`job-apply-${o.id.slice(0, 8)}`}
                  className="flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 shrink-0"
                >
                  <Send size={13} /> {t("jobs.apply")}
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </main>
      <Footer />

      {/* Modale de candidature */}
      {selected && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" role="dialog" aria-modal="true" data-testid="job-apply-modal">
          <div className="absolute inset-0 bg-black/85" onClick={() => setSelected(null)} />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-lg w-full max-h-[85vh] overflow-y-auto bg-[#121214] border border-[#C5A059]/40 p-8"
          >
            <button onClick={() => setSelected(null)} data-testid="job-apply-close" className="absolute top-5 right-5 text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Fermer">
              <X size={18} />
            </button>
            <p className="text-[#C5A059] text-[10px] uppercase tracking-[0.3em] mb-2">{t("jobs.apply")}</p>
            <h3 className="font-display text-xl uppercase font-bold pr-8">{selected.titre}</h3>
            <form onSubmit={postuler} className="mt-6 space-y-5">
              <input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder={t("jobs.form.name")} data-testid="job-form-nom" className="input-editorial w-full" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("jobs.form.email")} data-testid="job-form-email" className="input-editorial w-full" />
              <input required type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder={t("jobs.form.phone")} data-testid="job-form-telephone" className="input-editorial w-full" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("jobs.form.message")} rows={4} data-testid="job-form-message"
                className="w-full bg-transparent border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] transition-colors" />
              <div>
                <input ref={cvRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={uploadCv} className="hidden" data-testid="job-form-cv-input" />
                <button type="button" onClick={() => cvRef.current?.click()} disabled={uploading} data-testid="job-form-cv-button"
                  className="flex items-center gap-2 border border-[#27272A] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {form.cv ? t("jobs.form.cvok") : t("jobs.form.cv")}
                </button>
              </div>
              <button type="submit" disabled={sending} data-testid="job-form-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
                {t("jobs.form.send")}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
